import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { getWishlist, toggleWishlistItem } from "@/lib/api";
import { waLink } from "@/config/site";

const Wishlist = () => {
    const [items, setItems] = useState([]);
    useEffect(() => setItems(getWishlist()), []);

    const remove = (p) => {
        toggleWishlistItem(p);
        setItems(getWishlist());
        toast.success("Removed from wishlist");
    };

    const shareAll = () => {
        const lines = items.map((p) => `• ${p.name} — ${window.location.origin}/product/${p.slug}`).join("\n");
        window.open(waLink(`Hello BSK Jewellers, these are the pieces I have saved:\n${lines}`), "_blank");
    };

    return (
        <main className="pt-32 sm:pt-40 pb-24 min-h-screen">
            <Seo title="Your Wishlist | BSK Jewellers" siteName="BSK Jewellers" description="Your saved jewellery pieces at BSK Jewellers, Amritsar." />
            <div className="max-w-[1300px] mx-auto px-5 sm:px-8">
                <SectionHeading align="left" eyebrow="Saved Pieces" title={<>Your <span className="italic gold-text">Wishlist</span></>} />
                {items.length === 0 ? (
                    <Reveal>
                        <div className="text-center py-24 border border-gold/15 bg-charcoal/30" data-testid="wishlist-empty">
                            <Heart size={30} strokeWidth={1} className="text-gold mx-auto" />
                            <p className="font-serif italic text-2xl text-sand mt-6">Nothing saved yet.</p>
                            <Link to="/collections" data-testid="wishlist-browse-btn" className="inline-block mt-8 bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-9 py-4 hover:bg-gold-bright transition-colors">
                                Explore Collections
                            </Link>
                        </div>
                    </Reveal>
                ) : (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="wishlist-grid">
                            {items.map((p, i) => (
                                <Reveal key={p.slug} delay={(i % 4) * 0.06}>
                                    <div className="group relative border border-gold/10 hover:border-gold/50 transition-colors" data-testid={`wishlist-item-${p.slug}`}>
                                        <Link to={`/product/${p.slug}`}>
                                            <div className="aspect-[3/4] overflow-hidden">
                                                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                            </div>
                                            <div className="p-4">
                                                <p className="text-[10px] tracking-[0.25em] uppercase text-gold">{p.category}</p>
                                                <p className="font-serif text-lg text-ivory mt-1">{p.name}</p>
                                            </div>
                                        </Link>
                                        <button onClick={() => remove(p)} data-testid={`wishlist-remove-${p.slug}`} aria-label="Remove" className="absolute top-3 right-3 w-8 h-8 bg-obsidian/60 backdrop-blur border border-gold/25 flex items-center justify-center text-sand hover:text-gold transition-colors">
                                            <Trash2 size={14} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal className="mt-12 text-center">
                            <button onClick={shareAll} data-testid="wishlist-share-whatsapp-btn" className="inline-flex items-center gap-3 bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-9 py-4 hover:bg-gold-bright transition-colors">
                                <MessageCircle size={15} /> Share Wishlist on WhatsApp
                            </button>
                        </Reveal>
                    </>
                )}
            </div>
        </main>
    );
};

export default Wishlist;
